// src/features/links/SortPanel.tsx
// IR-1047 Phase 3.5 — a visible sort control under the filters (Rotem: users
// didn't realise the column headers were clickable). Drives the same sort/dir
// state as the header-click sort, so the two stay in sync.
// IR-1386 r2 (Rotem) — this row now also carries the Search box, FIRST in line;
// it used to be the leading cell of the 9-control filter grid above.
import { LinkSearchField, type MatchNav } from "./LinkSearchField";
import type { SortDir } from "./LinksTable";

interface Props {
  sort: string;
  dir: SortDir;
  onChange: (sort: string, dir: SortDir) => void;
  /** IR-1386 r2 — current search query; omitted only in sort-only usages. */
  search?: string;
  onSearchChange?: (q: string) => void;
  /** IR-1233 match navigation, rendered beside the search box. */
  matchNav?: MatchNav;
}

// Sort keys mirror the backend allowlist (_SORTABLE_COLUMNS) + the table columns.
const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: "created_at", label: "Date added" },
  { value: "link", label: "Link" },
  { value: "platform", label: "Platform" },
  { value: "appearances", label: "Appearances" },
  { value: "source_group_count", label: "Source Groups" },
  { value: "risk_score", label: "Risk Score" },
  { value: "classification", label: "Classification" },
  { value: "first_seen", label: "First Seen" },
  { value: "last_seen", label: "Last Seen" },
  { value: "operational_status", label: "Operational Status" },
  { value: "approval_required", label: "Approval Required" },
];

export function SortPanel({ sort, dir, onChange, search, onSearchChange, matchNav }: Props) {
  return (
    <div className="iris-card flex flex-wrap items-center gap-2 p-3">
      {onSearchChange && (
        <LinkSearchField value={search ?? ""} onChange={onSearchChange} matchNav={matchNav} />
      )}
      <span className="text-xs font-medium text-[var(--iris-fg-3)]">Sort by</span>
      <select
        aria-label="Sort by"
        value={sort}
        onChange={(e) => onChange(e.target.value, dir)}
        className="iris-input text-sm"
      >
        {SORT_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <div className="flex overflow-hidden rounded border border-[var(--iris-bg-4)]">
        {(["asc", "desc"] as const).map((d) => (
          <button
            key={d}
            type="button"
            aria-label={d === "asc" ? "Sort ascending" : "Sort descending"}
            aria-pressed={dir === d}
            onClick={() => onChange(sort, d)}
            className={
              dir === d
                ? "bg-[var(--iris-fg-1)] px-2 py-1 text-xs text-[var(--iris-bg-0)]"
                : "px-2 py-1 text-xs text-[var(--iris-fg-3)] hover:bg-[var(--iris-bg-3)]"
            }
          >
            {d === "asc" ? "A→Z ↑" : "Z→A ↓"}
          </button>
        ))}
      </div>
    </div>
  );
}
